import React from 'react';
import Icon from '../../../components/AppIcon';

const ResponseQualityMetrics = ({ auditData }) => {
  const qualityMetrics = {
    answerCompleteness: 94,
    photoCompliance: 89,
    onTimeSubmission: 76,
    detailLevel: 87,
    followUpRequired: 12
  };

  const incompleteResponses = [
    {
      id: 'R-045',
      user: 'Mike Wilson',
      question: 'Are all fire extinguishers properly mounted?',
      issue: 'Missing photo evidence',
      severity: 'medium',
      dateSubmitted: '2025-12-04'
    },
    {
      id: 'R-032',
      user: 'Sarah Chen',
      question: 'Describe any safety hazards observed',
      issue: 'Insufficient detail in description',
      severity: 'high',
      dateSubmitted: '2025-12-02'
    },
    {
      id: 'R-018',
      user: 'David Martinez',
      question: 'Rate the overall cleanliness',
      issue: 'No rating provided',
      severity: 'high',
      dateSubmitted: '2025-11-28'
    }
  ];

  const photoComplianceData = [
    {
      category: 'Fire Safety',
      required: 35,
      submitted: 31,
      compliance: 89
    },
    {
      category: 'Equipment Safety',
      required: 35,
      submitted: 28,
      compliance: 80
    },
    {
      category: 'PPE Compliance',
      required: 35,
      submitted: 33,
      compliance: 94
    },
    {
      category: 'General Safety',
      required: 35,
      submitted: 35,
      compliance: 100
    }
  ];

  const submissionTimeline = [
    { range: '0-2 days', count: 12, percentage: 34 },
    { range: '3-5 days', count: 15, percentage: 43 },
    { range: '6-10 days', count: 6, percentage: 17 },
    { range: '11+ days', count: 2, percentage: 6 }
  ];

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'bg-red-500/10 text-red-700 border-red-200',
      medium: 'bg-orange-500/10 text-orange-700 border-orange-200',
      low: 'bg-yellow-500/10 text-yellow-700 border-yellow-200'
    };
    return colors?.[severity] || colors?.low;
  };

  return (
    <div className="space-y-6">
      {/* Quality Metrics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Answer Completeness</span>
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {qualityMetrics?.answerCompleteness}%
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-green-500 h-1.5 rounded-full"
              style={{ width: `${qualityMetrics?.answerCompleteness}%` }}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Photo Compliance</span>
            <Icon name="Camera" size={16} color="var(--color-primary)" />
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {qualityMetrics?.photoCompliance}%
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-blue-500 h-1.5 rounded-full"
              style={{ width: `${qualityMetrics?.photoCompliance}%` }}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">On-time Submission</span>
            <Icon name="Clock" size={16} color="var(--color-warning)" />
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {qualityMetrics?.onTimeSubmission}%
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-orange-500 h-1.5 rounded-full"
              style={{ width: `${qualityMetrics?.onTimeSubmission}%` }}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Detail Level</span>
            <Icon name="FileText" size={16} color="var(--color-accent)" />
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {qualityMetrics?.detailLevel}%
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className="bg-purple-500 h-1.5 rounded-full"
              style={{ width: `${qualityMetrics?.detailLevel}%` }}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Follow-up Required</span>
            <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {qualityMetrics?.followUpRequired}
          </div>
          <div className="text-xs text-muted-foreground">responses flagged</div>
        </div>
      </div>
      {/* Photo Compliance by Category */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Photo Compliance by Category</h3>
        <div className="space-y-4">
          {photoComplianceData?.map((item, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">{item?.category}</span>
                  <span className="text-xs text-muted-foreground">
                    ({item?.submitted}/{item?.required} photos)
                  </span>
                </div>
                <span className="text-sm font-bold text-foreground">{item?.compliance}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    item?.compliance >= 90 ? 'bg-green-500' :
                    item?.compliance >= 75 ? 'bg-blue-500': 'bg-orange-500'
                  }`}
                  style={{ width: `${item?.compliance}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Submission Timeline */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Submission Timeline Distribution</h3>
        <div className="space-y-3">
          {submissionTimeline?.map((item, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="w-24 text-sm font-medium text-foreground">{item?.range}</div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">{item?.count} responses</span>
                  <span className="text-xs font-medium text-foreground">{item?.percentage}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full"
                    style={{ width: `${item?.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Incomplete Responses Requiring Follow-up */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Responses Requiring Follow-up</h3>
          <span className="px-3 py-1 bg-red-500/10 text-red-700 rounded-full text-sm font-medium">
            {incompleteResponses?.length} items
          </span>
        </div>
        
        <div className="space-y-3">
          {incompleteResponses?.map((response) => (
            <div
              key={response?.id}
              className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-foreground">{response?.user}</span>
                    <span className="px-2 py-0.5 bg-muted text-xs font-medium text-foreground rounded">
                      {response?.id}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getSeverityColor(response?.severity)}`}>
                      {response?.severity}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{response?.question}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Issue: {response?.issue}</span>
                    <span>Submitted: {response?.dateSubmitted}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-3 border-t border-border">
                <button className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium hover:bg-primary/90">
                  <Icon name="Send" size={14} />
                  Request Clarification
                </button>
                <button className="inline-flex items-center gap-1 px-3 py-1.5 bg-muted text-foreground rounded text-xs font-medium hover:bg-muted/80">
                  <Icon name="CheckCircle" size={14} />
                  Mark as Resolved
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quality Recommendations */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quality Improvement Recommendations</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-lg">
            <Icon name="Lightbulb" size={20} className="text-blue-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Photo Submission Reminders</div>
              <div className="text-xs text-muted-foreground">
                11% of responses are missing required photos. Consider adding in-app reminders during survey completion.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-purple-500/10 rounded-lg">
            <Icon name="MessageSquare" size={20} className="text-purple-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Response Detail Guidelines</div>
              <div className="text-xs text-muted-foreground">
                Provide examples of detailed vs. insufficient responses to help users understand expectations.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg">
            <Icon name="Award" size={20} className="text-green-500 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Recognize Timely Completion</div>
              <div className="text-xs text-muted-foreground">
                24% of users completed within 48 hours. Consider implementing recognition program for early completions.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseQualityMetrics;